import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Clock, 
  ShieldAlert, 
  Users, 
  Sparkles, 
  CheckCircle2, 
  X, 
  BarChart3, 
  FolderKanban,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Menu,
  FileText,
  Target,
  Zap,
  Play,
  TrendingUp,
  Calendar,
  PieChart,
  Layers,
  CheckSquare,
  AlertTriangle,
  Brain,
  GitBranch,
  ListChecks,
  Gauge,
  Shield,
  Rocket,
  Timer,
  LineChart
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);

  const features = [
    {
      id: 'zeiterfassung',
      title: "Zeiterfassung",
      shortDesc: "Rechtskonform & intuitiv",
      fullDesc: "Erfassen Sie Arbeitszeiten pro Projekt und Task mit einem Klick. Automatische Berechnung von Kosten und übersichtliche Reports.",
      icon: Clock,
      gridSpan: "md:col-span-2",
      color: "emerald",
      highlights: ["Ein-Klick Timer", "Kostenberechnung", "Team-Übersicht", "Export als CSV/PDF"],
      visual: "timer"
    },
    {
      id: 'risikomanagement',
      title: "Risikomanagement",
      shortDesc: "Gefahren früh erkennen",
      fullDesc: "Identifizieren, bewerten und überwachen Sie Projektrisiken mit Risikomatrix und KI-gestützten Massnahmenvorschlägen.",
      icon: ShieldAlert,
      gridSpan: "md:col-span-1",
      color: "red",
      accent: true,
      highlights: ["Risikomatrix", "Trend-Analyse", "KI-Empfehlungen", "Benachrichtigungen"],
      visual: "risk"
    },
    {
      id: 'ressourcenplanung',
      title: "Ressourcenplanung",
      shortDesc: "Kapazitäten optimal nutzen",
      fullDesc: "Behalten Sie die Auslastung Ihres Teams im Blick. Planen Sie Abwesenheiten und optimieren Sie die Ressourcenverteilung.",
      icon: Users,
      gridSpan: "md:col-span-1",
      color: "blue",
      highlights: ["Auslastungsübersicht", "Abwesenheitsplanung", "Kapazitätswarnung", "Team-Kalender"],
      visual: "capacity"
    },
    {
      id: 'ki-assistenz',
      title: "KI-Projektassistent",
      shortDesc: "Ihr intelligenter Partner",
      fullDesc: "Stellen Sie Fragen zu Ihren Projekten, lassen Sie Reports generieren und erhalten Sie intelligente Vorschläge.",
      icon: Sparkles,
      gridSpan: "md:col-span-2",
      color: "purple",
      highlights: ["Natürliche Sprache", "Projekt-Analysen", "Automatische Reports", "Prognosen"],
      visual: "ai"
    }
  ];

  const additionalFeatures = [
    { 
      id: 'kanban',
      icon: FolderKanban, 
      title: "Kanban & Gantt", 
      shortDesc: "Flexible Ansichten",
      fullDesc: "Wechseln Sie nahtlos zwischen Kanban-Board, Gantt-Chart und Listenansicht. Drag & Drop für schnelle Änderungen.",
      highlights: ["Kanban-Board", "Gantt-Chart", "Listenansicht", "Drag & Drop"],
      color: "indigo"
    },
    { 
      id: 'anforderungen',
      icon: FileText, 
      title: "Anforderungskatalog", 
      shortDesc: "Strukturierte Evaluationen",
      fullDesc: "Erstellen Sie Anforderungskataloge für Software-Evaluationen. Vergleichen Sie Anbieter und treffen Sie fundierte Entscheidungen.",
      highlights: ["Vorlagen", "Anbieter-Vergleich", "Bewertungsmatrix", "Vendor Portal"],
      color: "cyan"
    },
    { 
      id: 'businesscase',
      icon: Target, 
      title: "Business Cases", 
      shortDesc: "ROI berechnen",
      fullDesc: "Berechnen Sie den ROI Ihrer Projekte. Erstellen Sie überzeugende Business Cases mit automatischen Berechnungen.",
      highlights: ["ROI-Rechner", "Kosten-Nutzen", "Templates", "PDF-Export"],
      color: "orange"
    },
    { 
      id: 'kosten',
      icon: PieChart, 
      title: "Kostencontrolling", 
      shortDesc: "Budget im Griff",
      fullDesc: "Vergleichen Sie geplante und tatsächliche Kosten in Echtzeit. Erkennen Sie Budgetüberschreitungen frühzeitig.",
      highlights: ["Plan vs. Ist", "Prognosen", "Warnungen", "Dashboards"],
      color: "green"
    },
    { 
      id: 'change',
      icon: Layers, 
      title: "Change Management", 
      shortDesc: "Änderungen steuern",
      fullDesc: "Verwalten Sie Änderungsanträge strukturiert. Dokumentieren Sie Entscheidungen und kommunizieren Sie transparent.",
      highlights: ["Change Requests", "Genehmigungen", "Impact-Analyse", "Historie"],
      color: "pink"
    },
    { 
      id: 'meilensteine',
      icon: Calendar, 
      title: "Meilensteine", 
      shortDesc: "Termine im Blick",
      fullDesc: "Definieren Sie wichtige Meilensteine und verfolgen Sie deren Status. Automatische Erinnerungen halten Sie auf Kurs.",
      highlights: ["Timeline", "Erinnerungen", "Abhängigkeiten", "Status-Tracking"],
      color: "amber"
    }
  ];

  const plans = [
    {
      name: "Free",
      price: "0",
      desc: "Perfekt zum Kennenlernen",
      features: [
        { text: "3 Projekte", included: true },
        { text: "1 Benutzer", included: true },
        { text: "Basis-Projektansichten", included: true },
        { text: "Zeiterfassung", included: false },
        { text: "Risikomanagement", included: false },
        { text: "KI-Features", included: false }
      ],
      cta: "Kostenlos starten",
      popular: false
    },
    {
      name: "Pro",
      price: "19",
      desc: "Für professionelle Teams",
      features: [
        { text: "Unbegrenzte Projekte", included: true },
        { text: "1 User inklusive (+5/User)", included: true },
        { text: "Zeiterfassung", included: true },
        { text: "Risikomanagement", included: true },
        { text: "Ressourcenplanung", included: true },
        { text: "Change Management", included: true }
      ],
      cta: "14 Tage kostenlos",
      popular: true
    },
    {
      name: "Business",
      price: "29",
      desc: "Maximale Power",
      features: [
        { text: "Alles aus Pro", included: true },
        { text: "KI-Projektassistent", included: true },
        { text: "Anforderungskatalog", included: true },
        { text: "Erweiterte Reports", included: true },
        { text: "Priority Support", included: true }
      ],
      cta: "14 Tage kostenlos",
      popular: false
    }
  ];

  const faqs = [
    {
      q: "Kann ich Task2Plan kostenlos testen?",
      a: "Ja! Starten Sie mit dem Free-Plan oder testen Sie Pro/Business 14 Tage kostenlos. Keine Kreditkarte erforderlich."
    },
    {
      q: "Wo werden meine Daten gespeichert?",
      a: "Aktuell werden die Daten auf sicheren Cloud-Servern in Europa gespeichert. Die Migration auf Schweizer Rechenzentren ist im Laufe des Jahres 2026 geplant."
    },
    {
      q: "Kann ich weitere Benutzer hinzufügen?",
      a: "Ja, bei Pro und Business können Sie zusätzliche Benutzer für CHF 9/Monat pro User hinzufügen."
    },
    {
      q: "Wie funktioniert der KI-Assistent?",
      a: "Der KI-Assistent analysiert Ihre Projektdaten und beantwortet Fragen in natürlicher Sprache. Er gibt Empfehlungen und erstellt Reports automatisch."
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: { bg: "bg-emerald-100", text: "text-emerald-600", border: "border-emerald-200", gradient: "from-emerald-500 to-teal-500", light: "bg-emerald-50" },
      red: { bg: "bg-red-100", text: "text-red-600", border: "border-red-200", gradient: "from-red-500 to-orange-500", light: "bg-red-50" },
      blue: { bg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200", gradient: "from-blue-500 to-cyan-500", light: "bg-blue-50" },
      purple: { bg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200", gradient: "from-purple-500 to-pink-500", light: "bg-purple-50" },
      indigo: { bg: "bg-indigo-100", text: "text-indigo-600", border: "border-indigo-200", gradient: "from-indigo-500 to-blue-500", light: "bg-indigo-50" },
      cyan: { bg: "bg-cyan-100", text: "text-cyan-600", border: "border-cyan-200", gradient: "from-cyan-500 to-teal-500", light: "bg-cyan-50" },
      orange: { bg: "bg-orange-100", text: "text-orange-600", border: "border-orange-200", gradient: "from-orange-500 to-amber-500", light: "bg-orange-50" },
      green: { bg: "bg-green-100", text: "text-green-600", border: "border-green-200", gradient: "from-green-500 to-emerald-500", light: "bg-green-50" },
      pink: { bg: "bg-pink-100", text: "text-pink-600", border: "border-pink-200", gradient: "from-pink-500 to-rose-500", light: "bg-pink-50" },
      amber: { bg: "bg-amber-100", text: "text-amber-600", border: "border-amber-200", gradient: "from-amber-500 to-yellow-500", light: "bg-amber-50" }
    };
    return colors[color] || colors.emerald;
  };

  // Visual Components for Feature Cards
  const FeatureVisual = ({ type, color }) => {
    const colorClasses = getColorClasses(color);
    
    if (type === 'timer') {
      return (
        <div className="relative h-32 flex items-center justify-center">
          <div className={`w-24 h-24 rounded-full ${colorClasses.light} flex items-center justify-center relative`}>
            <div className="absolute inset-2 rounded-full border-4 border-emerald-200" />
            <div className="absolute inset-2 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" style={{ animationDuration: '3s' }} />
            <span className="text-2xl font-mono font-bold text-emerald-600">02:34</span>
          </div>
          <div className="absolute -right-2 -bottom-2 bg-white rounded-lg shadow-lg px-3 py-1 border">
            <span className="text-xs font-medium text-emerald-600">+CHF 127.50</span>
          </div>
        </div>
      );
    }
    
    if (type === 'risk') {
      return (
        <div className="relative h-32 flex items-center justify-center">
          <div className="grid grid-cols-3 gap-1">
            {[...Array(9)].map((_, i) => (
              <div 
                key={i} 
                className={`w-6 h-6 rounded ${
                  i < 3 ? 'bg-red-400' : i < 6 ? 'bg-yellow-400' : 'bg-green-400'
                } ${i === 1 ? 'animate-pulse' : ''}`}
              />
            ))}
          </div>
          <div className="absolute -right-2 top-0 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
            3 Hoch
          </div>
        </div>
      );
    }
    
    if (type === 'capacity') {
      return (
        <div className="relative h-32 flex items-center justify-center gap-2">
          {[85, 60, 95, 40].map((val, i) => (
            <div key={i} className="flex flex-col items-center gap-1">
              <div className="w-8 h-20 bg-slate-100 rounded-full overflow-hidden flex flex-col-reverse">
                <div 
                  className={`w-full rounded-full ${val > 90 ? 'bg-red-400' : val > 70 ? 'bg-yellow-400' : 'bg-blue-400'}`}
                  style={{ height: `${val}%` }}
                />
              </div>
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-[10px]">👤</span>
              </div>
            </div>
          ))}
        </div>
      );
    }
    
    if (type === 'ai') {
      return (
        <div className="relative h-32 flex items-center justify-center">
          <div className="bg-slate-100 rounded-2xl p-4 max-w-[200px]">
            <div className="flex items-start gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                <Brain className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1">
                <div className="h-2 bg-purple-200 rounded w-full mb-1" />
                <div className="h-2 bg-purple-200 rounded w-3/4" />
              </div>
            </div>
            <div className="flex gap-1 mt-3">
              <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-[10px] rounded-full">3 Risiken</span>
              <span className="px-2 py-0.5 bg-green-100 text-green-600 text-[10px] rounded-full">On Track</span>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Task2Plan
              </span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-emerald-600 font-medium transition-colors">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-emerald-600 font-medium transition-colors">Preise</a>
              <a href="#faq" className="text-slate-600 hover:text-emerald-600 font-medium transition-colors">FAQ</a>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                className="text-slate-600 hover:text-emerald-600 font-medium"
              >
                Anmelden
              </Button>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full px-6 shadow-lg shadow-emerald-500/25"
              >
                Kostenlos starten
              </Button>
            </div>

            <Button variant="ghost" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <Menu className="w-6 h-6" />
            </Button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-100">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Features</a>
                <a href="#pricing" className="text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>Preise</a>
                <a href="#faq" className="text-slate-600 font-medium" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
                <Button onClick={() => navigate('/login')} className="bg-emerald-600">Starten</Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - More Visual */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" />
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-emerald-100/40 via-teal-50/20 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 mb-6">
              <Zap className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">KI-gestütztes Projektmanagement</span>
            </div>
            
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1] mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Projekte planen.
              <br />
              <span className="bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500 bg-clip-text text-transparent">
                Einfach erfolgreich.
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Die All-in-One Lösung für KMUs – mit KI-Unterstützung.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button 
                size="lg"
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-full px-8 py-6 text-lg font-medium shadow-xl shadow-emerald-500/25 group"
              >
                Kostenlos starten
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Keine Kreditkarte</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>14 Tage kostenlos</span>
              </div>
            </div>
          </div>

          {/* Visual Dashboard Preview */}
          <div className="mt-16 relative max-w-5xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl blur-2xl opacity-10 scale-95" />
            <div className="relative bg-white rounded-3xl shadow-2xl border border-slate-200/50 overflow-hidden">
              {/* Browser Bar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1 bg-white rounded-lg text-xs text-slate-400 border">task2plan.com/dashboard</div>
                </div>
              </div>
              
              {/* Dashboard Content */}
              <div className="p-6 bg-gradient-to-br from-slate-50 to-white">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {/* Stats Cards */}
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <FolderKanban className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-xs text-slate-500">Projekte</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">12</p>
                    <p className="text-xs text-emerald-600">+2 diese Woche</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <CheckSquare className="w-4 h-4 text-blue-600" />
                      </div>
                      <span className="text-xs text-slate-500">Tasks</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">87</p>
                    <p className="text-xs text-blue-600">23 offen</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="text-xs text-slate-500">Zeit heute</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">6:42</p>
                    <p className="text-xs text-amber-600">Stunden</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="text-xs text-slate-500">On Track</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">94%</p>
                    <p className="text-xs text-purple-600">+5% vs. Vormonat</p>
                  </div>
                </div>

                {/* Project Progress */}
                <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
                  <h3 className="font-semibold text-slate-900 mb-4">Aktive Projekte</h3>
                  <div className="space-y-4">
                    {[
                      { name: "Website Relaunch", progress: 78, color: "emerald", tasks: "18/23" },
                      { name: "App Entwicklung", progress: 45, color: "blue", tasks: "34/76" },
                      { name: "Marketing Kampagne", progress: 92, color: "purple", tasks: "22/24" }
                    ].map((project, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <div className="w-32 font-medium text-sm text-slate-700 truncate">{project.name}</div>
                        <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-gradient-to-r ${project.color === 'emerald' ? 'from-emerald-400 to-teal-500' : project.color === 'blue' ? 'from-blue-400 to-cyan-500' : 'from-purple-400 to-pink-500'} rounded-full`}
                            style={{ width: `${project.progress}%` }}
                          />
                        </div>
                        <div className="text-sm font-semibold text-slate-700 w-12">{project.progress}%</div>
                        <div className="text-xs text-slate-500 w-16">{project.tasks} Tasks</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -left-8 top-1/3 bg-white rounded-2xl shadow-xl border p-4 animate-bounce-slow hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                  <Timer className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Timer läuft</p>
                  <p className="text-lg font-mono font-bold text-slate-900">02:34:17</p>
                </div>
              </div>
            </div>

            <div className="absolute -right-8 top-1/4 bg-white rounded-2xl shadow-xl border p-4 hidden lg:block">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-slate-700">KI-Tipp</span>
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-[150px]">3 Tasks könnten diese Woche kritisch werden</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 px-4 py-1">
              Features
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Alles in einer Lösung
            </h2>
          </div>

          {/* Main Features with Visuals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {features.map((feature, idx) => {
              const colorClasses = getColorClasses(feature.color);
              return (
                <Card 
                  key={idx}
                  onClick={() => setSelectedFeature(feature)}
                  className={`group relative overflow-hidden rounded-3xl bg-white p-6 border-2 border-slate-100 hover:border-${feature.color}-300 cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${feature.gridSpan}`}
                >
                  {feature.accent && (
                    <div className="absolute top-4 right-4 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  )}
                  
                  {/* Visual Element */}
                  <FeatureVisual type={feature.visual} color={feature.color} />
                  
                  <div className="mt-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 ${colorClasses.bg} rounded-xl flex items-center justify-center`}>
                        <feature.icon className={`w-5 h-5 ${colorClasses.text}`} />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900">{feature.title}</h3>
                    </div>
                    <p className="text-slate-600 text-sm">{feature.shortDesc}</p>
                  </div>
                  
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight className="w-5 h-5 text-emerald-600" />
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Additional Features - Now with Dialogs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {additionalFeatures.map((feature, idx) => {
              const colorClasses = getColorClasses(feature.color);
              return (
                <Card
                  key={idx} 
                  onClick={() => setSelectedFeature(feature)}
                  className="group flex flex-col items-center text-center p-5 rounded-2xl bg-white border-2 border-slate-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300 cursor-pointer"
                >
                  <div className={`w-14 h-14 ${colorClasses.light} rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <feature.icon className={`w-7 h-7 ${colorClasses.text}`} />
                  </div>
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">{feature.title}</h4>
                  <p className="text-xs text-slate-500">{feature.shortDesc}</p>
                  <div className="mt-2 text-xs text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Details <ArrowRight className="w-3 h-3" />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Visual Showcase Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                <Rocket className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Schneller Start</h3>
              <p className="text-slate-400">Registrieren und sofort loslegen</p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Sichere Daten</h3>
              <p className="text-slate-400">Verschlüsselte Übertragung</p>
            </div>
            <div>
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-500/20 flex items-center justify-center">
                <Gauge className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Regelmässige Updates</h3>
              <p className="text-slate-400">Neue Features & Verbesserungen</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 px-4 py-1">
              Preise
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Transparent & fair
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, idx) => (
              <Card 
                key={idx}
                className={`relative flex flex-col p-8 bg-white rounded-3xl border-2 transition-all duration-300 ${
                  plan.popular 
                    ? 'border-emerald-400 shadow-xl scale-105 z-10' 
                    : 'border-slate-200 hover:border-emerald-200 hover:shadow-xl'
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0">
                    Beliebt
                  </Badge>
                )}
                
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
                  <p className="text-sm text-slate-500">{plan.desc}</p>
                </div>

                <div className="mb-6">
                  <span className="text-5xl font-bold text-slate-900">CHF {plan.price}</span>
                  <span className="text-slate-500">/Monat</span>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, fIdx) => (
                    <li key={fIdx} className="flex items-center gap-3">
                      {feature.included ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      ) : (
                        <X className="w-5 h-5 text-slate-300 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${feature.included ? 'text-slate-700' : 'text-slate-400'}`}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => navigate('/login')}
                  className={`w-full rounded-full py-6 font-medium ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg' 
                      : 'bg-white text-slate-900 border-2 border-slate-200 hover:border-emerald-300'
                  }`}
                >
                  {plan.cta}
                </Button>
              </Card>
            ))}
          </div>

          <p className="text-center mt-8 text-slate-500 text-sm">
            Alle Preise exkl. MwSt. • Jährlich 20% sparen
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-slate-100 text-slate-700 border-slate-200 px-4 py-1">
              FAQ
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Häufige Fragen
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div 
                key={idx}
                className="border-2 border-slate-100 rounded-2xl overflow-hidden hover:border-emerald-200 transition-colors"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-semibold text-slate-900 pr-4">{faq.q}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${openFaq === idx ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                    {openFaq === idx ? <ChevronUp className="w-5 h-5 text-emerald-600" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-6 text-slate-600">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bereit für besseres Projektmanagement?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Starten Sie heute kostenlos.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/login')}
            className="bg-white text-emerald-700 hover:bg-emerald-50 rounded-full px-10 py-7 text-lg font-semibold shadow-2xl group"
          >
            Jetzt kostenlos starten
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Task2Plan</span>
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#features" className="hover:text-emerald-400">Features</a>
              <a href="#pricing" className="hover:text-emerald-400">Preise</a>
              <a href="#" className="hover:text-emerald-400">Impressum</a>
              <a href="#" className="hover:text-emerald-400">Datenschutz</a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            © 2026 Task2Plan. Made with ❤️ für KMUs.
          </div>
        </div>
      </footer>

      {/* Feature Detail Dialog */}
      <Dialog open={!!selectedFeature} onOpenChange={() => setSelectedFeature(null)}>
        <DialogContent className="sm:max-w-lg">
          {selectedFeature && (
            <>
              <DialogHeader>
                <div className={`w-14 h-14 ${getColorClasses(selectedFeature.color).bg} rounded-2xl flex items-center justify-center mb-4`}>
                  <selectedFeature.icon className={`w-7 h-7 ${getColorClasses(selectedFeature.color).text}`} />
                </div>
                <DialogTitle className="text-2xl">{selectedFeature.title}</DialogTitle>
                <DialogDescription className="text-base text-slate-600 mt-2">
                  {selectedFeature.fullDesc}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <h4 className="font-semibold text-slate-900 mb-3">Highlights:</h4>
                <div className="grid grid-cols-2 gap-2">
                  {selectedFeature.highlights.map((highlight, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      <span className="text-sm text-slate-700">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="mt-6">
                <Button 
                  onClick={() => { setSelectedFeature(null); navigate('/login'); }}
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full py-6"
                >
                  Jetzt ausprobieren
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
